/**
 * Stored referral from a `/r/:ref` or `?ref=` visit. First touch wins (spec:
 * "first valid referral wins"): a stored, unexpired ref is never overwritten.
 * Every access is guarded — storage can be unavailable (private mode, blocked).
 */
const KEY = 'cyberlete_referral'
const TTL_MS = 90 * 24 * 60 * 60 * 1000
const VALID = /^[A-Za-z0-9_.-]{2,64}$/

interface StoredReferral {
  ref: string
  savedAt: number
  // User id whose account-level claim was already attempted, so we don't retry a
  // claim the backend will refuse (e.g. account too old) on every app load. The ref
  // itself is kept — it still prefills the create-community referral field.
  claimTriedBy?: string
}

function read(): StoredReferral | null {
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as StoredReferral
    if (!parsed?.ref || Date.now() - parsed.savedAt > TTL_MS) {
      localStorage.removeItem(KEY)
      return null
    }
    return parsed
  } catch {
    return null
  }
}

export function getStoredReferral(): string | null {
  return read()?.ref ?? null
}

export function wasClaimTriedBy(userId: string): boolean {
  return read()?.claimTriedBy === userId
}

export function markClaimTried(userId: string): void {
  const cur = read()
  if (!cur) return
  try {
    localStorage.setItem(KEY, JSON.stringify({ ...cur, claimTriedBy: userId }))
  } catch {
    /* ignore */
  }
}

export function storeReferral(ref: string | null | undefined): void {
  const clean = (ref || '').trim()
  if (!VALID.test(clean) || getStoredReferral()) return
  try {
    localStorage.setItem(KEY, JSON.stringify({ ref: clean, savedAt: Date.now() } as StoredReferral))
  } catch {
    /* storage unavailable — referral just won't persist */
  }
}

export function clearStoredReferral(): void {
  try {
    localStorage.removeItem(KEY)
  } catch {
    /* ignore */
  }
}

/** Reads a ref from the current URL (`/r/:ref` or `?ref=`), synchronously. */
export function referralFromLocation(loc: { pathname: string; search: string }): string | null {
  const m = loc.pathname.match(/^\/r\/([^/?#]+)/)
  if (m) return decodeURIComponent(m[1])
  try {
    return new URLSearchParams(loc.search).get('ref')
  } catch {
    return null
  }
}
